import CreateAssetSet from '@/components/features/assets/CreateAssetSet'
import MultiFileSelect from '@/components/features/assets/MultiFileSelect'
import MultiFileSelection from '@/components/features/assets/MultiFileSelection'
import OverlapWarningDialog from '@/components/features/assets/OverlapWarningDialog'
import ImageLocationMap from '@/components/features/maps/ImageLocationMap'
import { Form } from '@/components/forms/Form'
import {
  SelectController,
  TextController,
} from '@/components/forms/FormController'
import { FormSection } from '@/components/forms/FormSection'
import PageHeader from '@/components/layout/PageHeader'
import { useClient } from '@/context/ClientContext'
import { useOverlapVerdict } from '@/hooks/useOverlapVerdict'
import {
  ImagePositionData,
  convertImagesWithMetadataToPositionData,
} from '@/lib/geoUtils'
import { extractMetadataFromImages } from '@/lib/metadataExtraction'
import { zodResolver } from '@/lib/zod'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { z } from 'zod'

import { AssetSetType } from '@droidmap/asset-service-contract'
import { Button } from '@droidmap/frontend-ui'
import { overlapNeedsAcknowledgement } from '@droidmap/shared-utils'

export const Route = createFileRoute(
  '/_authenticated/orgs/$orgId_/projects/$projectId_/_pathless/asset-sets/create',
)({
  component: AssetSetCreate,
  loader: async ({ context, params }) => {
    const { metadata } = context
    return {
      crumb: 'Create Asset Set',
      orgId: params.orgId,
      projectId: params.projectId,
      metadata,
    }
  },
})

// Zod schema for asset set creation
const assetSetCreateSchema = z.object({
  assetSetName: z
    .string()
    .min(1, { error: 'Asset set name is required' })
    .max(64, { error: 'Must be 64 or fewer characters long' }),
  assetSetType: z.enum(AssetSetType),
  files: z
    .array(z.instanceof(File))
    .min(1, { error: 'At least one file must be selected' }),
})

type AssetSetCreateForm = z.infer<typeof assetSetCreateSchema>

function AssetSetCreate() {
  const { orgId, projectId, metadata } = Route.useLoaderData()
  const { projectClient } = useClient()
  const {
    data: project,
    isError: isProjectError,
    error: projectError,
    isLoading: isLoadingProject,
  } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectClient.getProject({ projectId }, metadata),
  })
  const navigate = useNavigate()
  const form = useForm<AssetSetCreateForm>({
    resolver: zodResolver(assetSetCreateSchema),
    defaultValues: {
      assetSetName: '',
      assetSetType: AssetSetType.DroneImages,
      files: [],
    },
    mode: 'onBlur',
  })
  const {
    control,
    formState: { errors, isSubmitting },
    setValue,
  } = form
  // `useWatch` rather than `watch()`: it subscribes to just this field and
  // returns a plain value, where `watch()` returns a function whose identity
  // react-hook-form cannot keep stable — which is why the React Compiler skips
  // any component that calls it. It also makes the dependency below a real
  // value instead of a call re-evaluated on every render.
  const assetSetType = useWatch({ control, name: 'assetSetType' })
  const assetSetName = useWatch({ control, name: 'assetSetName' })
  // File selection state (for preview and GPS extraction)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [isCreating, setIsCreating] = useState(false)
  const [imagesWithPosition, setImagesWithPosition] = useState<
    ImagePositionData[]
  >([])
  const [isExtractingPosition, setIsExtractingPosition] = useState(false)
  // Overlap is judged from the EXIF already read for the preview map, before
  // a byte is uploaded (droidmap#3019). Files without GPS never become pins,
  // so the selection — not the pin count — is the set's size.
  const overlap = useOverlapVerdict(imagesWithPosition, selectedFiles.length)
  const [isOverlapWarningOpen, setIsOverlapWarningOpen] = useState(false)
  // Sync file selection with form
  const handleFileChange = (files: File[]) => {
    setSelectedFiles(files)
    setValue('files', files, { shouldValidate: true })
  }
  // Extract position data when files are selected and asset type is drone images
  useEffect(() => {
    const extractPosition = async () => {
      if (
        selectedFiles.length > 0 &&
        assetSetType === AssetSetType.DroneImages
      ) {
        setIsExtractingPosition(true)
        try {
          const imagesWithMetadata =
            await extractMetadataFromImages(selectedFiles)
          setImagesWithPosition(
            convertImagesWithMetadataToPositionData(imagesWithMetadata),
          )
        } catch (error) {
          console.error('Error extracting position data:', error)
          setImagesWithPosition([])
        } finally {
          setIsExtractingPosition(false)
        }
      } else {
        setImagesWithPosition([])
      }
    }
    extractPosition()
  }, [selectedFiles, assetSetType])
  if (isLoadingProject) {
    return <div>Loading...</div>
  }
  if (isProjectError) {
    return <div>Error: {projectError?.message}</div>
  }
  if (!project) {
    return <div>Project not found</div>
  }

  // Creating waits for the verdict: while the EXIF is still being read the
  // pins are the previous selection's, and a large set is estimated
  // off-thread — either way a click now would upload with no warning.
  const isCheckingOverlap =
    assetSetType === AssetSetType.DroneImages &&
    (isExtractingPosition || overlap.pending)

  const onSubmit = async (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    event.preventDefault()
    if (isCheckingOverlap) return
    const isValid = await form.trigger()
    if (!isValid) return
    // The gate sits between validation and the upload, so declining it
    // uploads nothing. No tick box: creating a set costs no compute.
    if (
      assetSetType === AssetSetType.DroneImages &&
      overlap.verdict &&
      overlapNeedsAcknowledgement(overlap.verdict)
    ) {
      setIsOverlapWarningOpen(true)
      return
    }
    setIsCreating(true)
  }

  if (isCreating) {
    return (
      <div>
        <PageHeader
          title="Create Asset Set"
          subtitle={`Project: ${project.name}`}
        />
        <Form>
          <FormSection header="Uploading Files">
            <CreateAssetSet
              files={selectedFiles}
              assetSetName={assetSetName}
              projectId={projectId}
              metadata={metadata}
              assetSetType={assetSetType as AssetSetType}
              onComplete={() =>
                navigate({
                  to: `/orgs/${orgId}/projects/${projectId}/asset-sets`,
                })
              }
              onCancel={() =>
                navigate({
                  to: `/orgs/${orgId}/projects/${projectId}/asset-sets`,
                })
              }
            />
          </FormSection>
        </Form>
      </div>
    )
  }
  return (
    <div>
      <PageHeader
        title="Create Asset Set"
        subtitle={`Project: ${project.name}`}
      />
      <Form>
        <FormSection className="space-y-4" header="Asset Set Details">
          <TextController
            name="assetSetName"
            control={control}
            label="Asset Set Name"
            errors={errors}
          />
          <SelectController
            name="assetSetType"
            control={control}
            label="Asset Type"
            errors={errors}
            optionsNamed={[
              { name: 'Drone Images', value: AssetSetType.DroneImages },
              { name: 'Lidar', value: AssetSetType.Lidar },
              { name: 'Map Layers', value: AssetSetType.MapLayers },
              { name: 'Other', value: AssetSetType.Other },
            ]}
          />
          <div className="space-y-2">
            <label className="text-sm">File Selection</label>
            <MultiFileSelect
              onFileChange={handleFileChange}
              files={selectedFiles}
            />
            {selectedFiles.length > 0 && (
              <MultiFileSelection
                files={selectedFiles}
                onClear={() => handleFileChange([])}
              />
            )}
            {errors.files?.message && (
              <p className="mt-1 text-sm font-bold text-danger">
                {errors.files.message as string}
              </p>
            )}
          </div>
        </FormSection>
        {selectedFiles.length > 0 &&
          assetSetType === AssetSetType.DroneImages && (
            <FormSection header="Image Locations">
              {isExtractingPosition ? (
                <div className="flex items-center justify-center h-64 bg-panel-2 rounded-lg">
                  <p className="text-text-dim">
                    Extracting GPS data from images...
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-text-dim">
                      {imagesWithPosition.length} of {selectedFiles.length}{' '}
                      images contain position data
                    </p>
                  </div>
                  <ImageLocationMap
                    imagesWithPosition={imagesWithPosition}
                    className="rounded-lg border border-border"
                    overlap={overlap}
                  />
                </div>
              )}
            </FormSection>
          )}
        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            onClick={() =>
              navigate({
                to: `/orgs/${orgId}/projects/${projectId}/asset-sets`,
              })
            }
          >
            Cancel
          </Button>
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
              onSubmit(event)
            }
            disabled={isSubmitting || isCheckingOverlap}
            variant="creative"
          >
            {isCheckingOverlap ? 'Checking images…' : 'Create Asset Set'}
          </Button>
        </div>
      </Form>
      {overlap.verdict && (
        <OverlapWarningDialog
          verdict={overlap.verdict}
          isOpen={isOverlapWarningOpen}
          onCancel={() => setIsOverlapWarningOpen(false)}
          onProceed={() => {
            setIsOverlapWarningOpen(false)
            setIsCreating(true)
          }}
          proceedLabel="Upload anyway"
        />
      )}
    </div>
  )
}
